import React, { useState, useEffect, useRef, useCallback } from 'react';

// AW Symbol Placement tab.
// Proposes data-drop symbols on a plan (PDF / DXF / Revit model JSON). A person approves every
// placement before anything is exported. Talks only to /api/symbol-placement/* on the backend.

const API = 'https://apispeccheck.zonetap.com/api/symbol-placement';

// Same idea as checkCodePlusAccess: ask the backend (not the generic feature list) whether this
// user's firm has the feature. Returns the access object, or null if not enabled / unreachable.
export async function checkSymbolPlacementAccess(getToken) {
  try {
    const token = getToken();
    if (!token) return null;
    const r = await fetch(API + '/access', { headers: { Authorization: 'Bearer ' + token } });
    if (!r.ok) return null;
    const a = await r.json();
    return a && a.enabled ? a : null;
  } catch (e) {
    return null;
  }
}

async function api(getToken, path, opts = {}) {
  const headers = { Authorization: 'Bearer ' + getToken(), ...(opts.headers || {}) };
  const r = await fetch(API + path, { ...opts, headers });
  if (!r.ok) {
    let msg = 'Request failed (' + r.status + ')';
    try { const j = await r.json(); if (j && (j.error || j.message)) msg = j.error || j.message; } catch (e) { /* not json */ }
    const err = new Error(msg); err.status = r.status; throw err;
  }
  return r;
}

async function saveBlob(getToken, path, fallbackName) {
  const r = await api(getToken, path);
  const blob = await r.blob();
  let name = fallbackName;
  const cd = r.headers.get('Content-Disposition') || '';
  const m = /filename="?([^";]+)"?/i.exec(cd);
  if (m) name = m[1];
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

const fmtMb = (n) => (n < 1024 ? n + ' B' : n < 1048576 ? Math.round(n / 1024) + ' KB' : (n / 1048576).toFixed(1) + ' MB');

// Cross-origin responses hide the server's Content-Disposition name, so name downloads here.
const FILE_EXT = { approved_marked_pdf: ['approved', 'pdf'], approved_overlay_dxf: ['approved', 'dxf'], approved_csv: ['approved-drops', 'csv'], approved_revit_json: ['revit-placements', 'json'] };
function downloadName(job, k) {
  const base = String((job && job.filename) || 'plan').replace(/\.[^.]+$/, '');
  const m = FILE_EXT[k] || [k, 'bin'];
  return base + '-' + m[0] + '.' + m[1];
}
const KIND_LABEL = { pdf: 'PDF', dxf: 'DXF', json: 'Revit model' };
const FILE_LABEL = {
  approved_marked_pdf: 'Marked-up PDF', approved_overlay_dxf: 'DXF overlay',
  approved_revit_json: 'Revit placements (.json)', approved_csv: 'Drop list (.csv)',
};

const st = {
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 },
  h2: { fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 },
  h3: { fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 10px' },
  sub: { fontSize: 13, color: '#6b7280', lineHeight: 1.5, margin: '6px 0 0' },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' },
  label: { display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, minWidth: 90 },
  btn: { padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#111827', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnPrimary: { padding: '9px 16px', borderRadius: 8, border: '1px solid #D4AF37', background: '#D4AF37', color: '#111827', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnOff: { opacity: 0.5, cursor: 'not-allowed' },
  pill: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 },
  err: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 },
  warn: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 },
  ok: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 },
  th: { textAlign: 'left', fontSize: 11, color: '#6b7280', fontWeight: 600, padding: '6px 8px', borderBottom: '1px solid #e5e7eb', textTransform: 'uppercase' },
  td: { fontSize: 13, color: '#111827', padding: '7px 8px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' },
};

const STATUS_STYLE = {
  approved: { background: '#dcfce7', color: '#166534' },
  rejected: { background: '#fee2e2', color: '#991b1b' },
  pending: { background: '#f3f4f6', color: '#374151' },
};

export default function SymbolPlacementTab({ user, getAuthToken }) {
  const [access, setAccess] = useState(null);
  const [file, setFile] = useState(null);
  const [scale, setScale] = useState('');
  const [page, setPage] = useState('1');
  const [level, setLevel] = useState('');
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [profiles, setProfiles] = useState([]);   // disciplines: Telecom, Electrical, Mechanical, Plumbing
  const [profile, setProfile] = useState('tel');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [job, setJob] = useState(null);       // full job (with proposals when done)
  const [recent, setRecent] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [exported, setExported] = useState([]); // approved_* file kinds available
  const [builds, setBuilds] = useState([]);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [cfg, setCfg] = useState(null);           // firm rules/options for the selected discipline
  const [rulesMsg, setRulesMsg] = useState(null); // { ok, text }
  const rulesInput = useRef(null);
  const [editing, setEditing] = useState(false);
  const [famName, setFamName] = useState('');
  const [famType, setFamType] = useState('');
  const pollRef = useRef(null);
  const fileInput = useRef(null);

  const ext = file ? file.name.split('.').pop().toLowerCase() : '';

  const loadAccess = useCallback(async () => {
    try { const r = await api(getAuthToken, '/access'); setAccess(await r.json()); } catch (e) { /* tab is hidden by the parent if no access */ }
  }, [getAuthToken]);

  const loadRecent = useCallback(async () => {
    try { const r = await api(getAuthToken, '/jobs'); setRecent((await r.json()).jobs || []); } catch (e) { /* ignore */ }
  }, [getAuthToken]);

  useEffect(() => {
    loadAccess(); loadRecent();
    api(getAuthToken, '/profiles').then((r) => r.json()).then((d) => { setProfiles(d.profiles || []); if (d.default) setProfile(d.default); }).catch(() => {});
    api(getAuthToken, '/addin/builds').then((r) => r.json()).then((d) => setBuilds(d.builds || [])).catch(() => {});
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [getAuthToken, loadAccess, loadRecent]);

  useEffect(() => { if (rulesMsg && rulesMsg.ok) { const t = setTimeout(() => setRulesMsg(null), 5000); return () => clearTimeout(t); } return undefined; }, [rulesMsg]);

  const loadCfg = useCallback(async (pid) => {
    try { const r = await api(getAuthToken, '/config?profile=' + encodeURIComponent(pid)); const c = await r.json(); setCfg(c); setFamName((c.options && c.options.family_name) || ''); setFamType((c.options && c.options.family_type) || ''); } catch (e) { setCfg(null); }
  }, [getAuthToken]);
  useEffect(() => { if (rulesOpen && profile) { setRulesMsg(null); setEditing(false); loadCfg(profile); } }, [rulesOpen, profile, loadCfg]);

  // options the firm has changed from the discipline defaults (only these are saved)
  function customOptions(c) {
    const out = {};
    Object.keys(c.options || {}).forEach((k) => { if (JSON.stringify(c.options[k]) !== JSON.stringify((c.defaultOptions || {})[k])) out[k] = c.options[k]; });
    return out;
  }
  async function saveCfg(body, okText) {
    setRulesMsg(null);
    try {
      await api(getAuthToken, '/config', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ profile, ...body }) });
      await loadCfg(profile);
      const r = await api(getAuthToken, '/profiles'); setProfiles((await r.json()).profiles || []);
      setRulesMsg({ ok: true, text: okText });
    } catch (e) { setRulesMsg({ ok: false, text: e.message }); }
  }
  async function downloadRules() {
    try { await saveBlob(getAuthToken, '/config/export?profile=' + encodeURIComponent(profile), 'placement-rules-' + profile + '.json'); }
    catch (e) { setRulesMsg({ ok: false, text: e.message }); }
  }
  async function uploadRules(f) {
    if (!f || !cfg) return;
    let j;
    try { j = JSON.parse(await f.text()); } catch (e) { setRulesMsg({ ok: false, text: 'That file is not valid JSON.' }); return; }
    if (!j || j.schema !== 'aecworks.placement-rules/1' || !Array.isArray(j.rules)) { setRulesMsg({ ok: false, text: 'Not an AECWorks rules file (expected schema aecworks.placement-rules/1). Use "Download rules" to get a template.' }); return; }
    if (j.profile && j.profile !== profile && !window.confirm('This file was exported for "' + (j.label || j.profile) + '", but you are on a different discipline. Import it here anyway?')) return;
    const keep = (cfg.editableOptions || []);
    const opts = { ...customOptions(cfg) };
    Object.keys(j.options || {}).forEach((k) => { if (keep.includes(k)) opts[k] = j.options[k]; });
    await saveCfg({ rules: j.rules, options: opts }, 'Rules imported (' + j.rules.length + ' rules).');
  }
  async function saveEdited(rules, spacing) {
    const o = { ...customOptions(cfg) };
    Object.keys(spacing).forEach((k) => { if (spacing[k] == null) delete o[k]; else o[k] = spacing[k]; });
    await saveCfg({ rules, options: o }, 'Rules saved (' + rules.length + ' rules).');
    setEditing(false);
  }
  async function resetRules() {
    if (!window.confirm('Go back to the built-in rules for this discipline? Your custom rules are removed.')) return;
    await saveCfg({ resetRules: true, options: customOptions(cfg) }, 'Back to built-in rules.');
  }
  async function setFallback(v) {
    const o = customOptions(cfg); if (v === 'rules') delete o.model_fallback; else o.model_fallback = v;
    await saveCfg({ options: o }, 'Saved.');
  }

  async function saveFamily() {
    const o = customOptions(cfg);
    if (famName.trim()) o.family_name = famName.trim(); else delete o.family_name;
    if (famType.trim()) o.family_type = famType.trim(); else delete o.family_type;
    await saveCfg({ options: o }, famName.trim() ? 'Revit family saved.' : 'Revit family cleared. Revit will ask which family to use.');
  }

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const loadPreview = useCallback(async (j) => {
    setPreviewUrl('');
    if (!j || !(j.files || []).includes('preview_png')) return;
    try {
      const r = await api(getAuthToken, '/jobs/' + j.jobId + '/files/preview_png');
      setPreviewUrl(URL.createObjectURL(await r.blob()));
    } catch (e) { /* preview is optional */ }
  }, [getAuthToken]);

  const fetchJob = useCallback(async (jobId) => {
    const r = await api(getAuthToken, '/jobs/' + jobId);
    return r.json();
  }, [getAuthToken]);

  // Poll while queued / running.
  const watch = useCallback((jobId) => {
    if (pollRef.current) clearTimeout(pollRef.current);
    const tick = async () => {
      try {
        const j = await fetchJob(jobId);
        setJob(j);
        if (j.status === 'queued' || j.status === 'running') { pollRef.current = setTimeout(tick, 2000); return; }
        if (j.status === 'done') { loadPreview(j); setExported((j.files || []).filter((k) => k.startsWith('approved_'))); }
        loadAccess(); loadRecent();
      } catch (e) { setError(e.message); }
    };
    tick();
  }, [fetchJob, loadPreview, loadAccess, loadRecent]);

  async function submit() {
    setError(''); setNotice('');
    if (!file) { setError('Choose a PDF, DXF or Revit model (.json) file first.'); return; }
    if (ext === 'dwg') { setError('DWG files must be converted to DXF first.'); return; }
    if (!['pdf', 'dxf', 'json'].includes(ext)) { setError('Supported files: PDF, DXF, or a Revit model export (.json).'); return; }
    if (access && access.maxUploadMb && file.size > access.maxUploadMb * 1048576) {
      setError('That file is ' + fmtMb(file.size) + '. Your plan allows up to ' + access.maxUploadMb + ' MB.'); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('profile', profile);
      if (scale.trim()) fd.append('scale', scale.trim());
      if (ext === 'pdf' && page) fd.append('page', String(Math.max(0, (parseInt(page, 10) || 1) - 1))); // UI is 1-based, API is 0-based
      if (ext === 'json' && level.trim()) fd.append('level', level.trim());
      const r = await api(getAuthToken, '/jobs', { method: 'POST', body: fd });
      const d = await r.json();
      setJob({ jobId: d.jobId, status: d.status, queue: d.queue, filename: file.name });
      setExported([]); setPreviewUrl('');
      watch(d.jobId);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  }

  async function openJob(jobId) {
    setError(''); setNotice('');
    try { const j = await fetchJob(jobId); setJob(j); setExported((j.files || []).filter((k) => k.startsWith('approved_'))); if (j.status === 'done') loadPreview(j); else if (j.status !== 'failed') watch(jobId); } catch (e) { setError(e.message); }
  }

  async function decide(decisions, bulk) {
    if (!job) return;
    setError('');
    // optimistic update
    if (decisions) {
      const map = new Map(decisions.map((d) => [d.id, d.status]));
      setJob((j) => ({ ...j, proposals: j.proposals.map((p) => (map.has(p.id) ? { ...p, status: map.get(p.id) } : p)) }));
    }
    try {
      await api(getAuthToken, '/jobs/' + job.jobId + '/decisions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bulk ? { bulk } : { decisions }),
      });
      if (bulk) setJob(await fetchJob(job.jobId));
      else setJob((j) => ({ ...j, approvals: countApprovals(j.proposals) }));
      loadRecent();
    } catch (e) { setError(e.message); setJob(await fetchJob(job.jobId)); }
  }

  async function doExport() {
    setError(''); setNotice(''); setBusy(true);
    try {
      const r = await api(getAuthToken, '/jobs/' + job.jobId + '/export', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const d = await r.json();
      setExported(d.files || []);
      setNotice(d.count + ' approved placement' + (d.count === 1 ? '' : 's') + ' exported. Download below.');
      loadRecent();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  async function removeJob(jobId) {
    try { await api(getAuthToken, '/jobs/' + jobId, { method: 'DELETE' }); if (job && job.jobId === jobId) { setJob(null); setPreviewUrl(''); } loadRecent(); } catch (e) { setError(e.message); }
  }

  const proposals = (job && job.proposals) || [];
  // Don't repeat a skipped room in the yellow warnings when it is already listed under "Needs a human look".
  const shownWarnings = ((job && job.summary && job.summary.warnings) || []).filter((w) => {
    const t = typeof w === 'string' ? w : JSON.stringify(w);
    return !((job.flagged || []).some((f) => f.reason && t.includes(f.reason)));
  });
  const approvals = job && job.status === 'done' ? countApprovals(proposals) : { approved: 0, rejected: 0, pending: 0 };
  const byRoom = {};
  proposals.forEach((p) => { const k = (p.room_number || '') + '|' + (p.room_name || ''); (byRoom[k] = byRoom[k] || []).push(p); });
  const limitReached = access && access.monthlyLimit > 0 && access.usedThisMonth >= access.monthlyLimit;
  const running = job && (job.status === 'queued' || job.status === 'running');

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div style={st.card}>
        <h2 style={st.h2}>📍 Symbol Placement</h2>
        <p style={st.sub}>
          Upload a floor plan, choose the discipline, and we propose where outlets and devices go using your firm’s rules. Nothing is placed
          until you approve it, and only approved placements are exported.
        </p>
        {access && access.monthlyLimit > 0 && (
          <p style={{ ...st.sub, color: limitReached ? '#b91c1c' : '#6b7280' }}>
            {limitReached ? 'Monthly limit reached. Contact us to raise it.' : access.usedThisMonth + ' of ' + access.monthlyLimit + ' runs used this month.'}
          </p>
        )}
      </div>

      {error && <div style={st.err}>{error}</div>}
      {notice && <div style={st.ok}>{notice}</div>}

      <div style={st.card}>
        <h3 style={st.h3}>1. Upload a plan</h3>
        {profiles.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={st.label}>Discipline</label>
            <select style={{ ...st.input, minWidth: 280 }} value={profile} onChange={(e) => setProfile(e.target.value)} disabled={busy || running}>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.label}{p.status === 'starter' ? ' – starter rules' : ''}</option>)}
            </select>
            {(profiles.find((p) => p.id === profile) || {}).status === 'starter' && (
              <div style={{ ...st.warn, marginTop: 8, marginBottom: 0 }}>{(profiles.find((p) => p.id === profile) || {}).note}</div>
            )}
          </div>
        )}
        <div style={st.row}>
          <label
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) setFile(f); }}
            style={{ flex: '1 1 320px', minWidth: 260, display: 'block', textAlign: 'center', cursor: 'pointer', padding: '22px 16px', borderRadius: 12,
                     border: '2px dashed ' + (drag ? '#D4AF37' : '#d1d5db'), background: drag ? '#fffbeb' : '#f9fafb', color: '#6b7280', fontSize: 13 }}>
            <input ref={fileInput} type="file" accept=".pdf,.dxf,.json,.dwg" style={{ display: 'none' }}
              onChange={(e) => setFile(e.target.files[0] || null)} />
            {file
              ? <span style={{ color: '#111827', fontWeight: 600 }}>📄 {file.name} <span style={{ color: '#6b7280', fontWeight: 400 }}>({fmtMb(file.size)}). Click to change</span></span>
              : <span>Drag &amp; drop a floor plan here or click to select<br /><span style={{ fontSize: 11 }}>PDF, DXF or Revit model (.json)</span></span>}
          </label>
          <div>
            <label style={st.label}>Scale (optional)</label>
            <input style={st.input} placeholder="3/32 or 1/8" value={scale} onChange={(e) => setScale(e.target.value)} />
          </div>
          {ext === 'pdf' && (
            <div>
              <label style={st.label}>Page</label>
              <input style={{ ...st.input, width: 70, minWidth: 0 }} type="number" min="1" value={page} onChange={(e) => setPage(e.target.value)} />
            </div>
          )}
          {ext === 'json' && (
            <div>
              <label style={st.label}>Level (optional)</label>
              <input style={st.input} placeholder="Level 2" value={level} onChange={(e) => setLevel(e.target.value)} />
            </div>
          )}
          <button style={{ ...st.btnPrimary, ...((busy || running || limitReached) ? st.btnOff : {}) }} disabled={busy || running || limitReached} onClick={submit}>
            {busy ? 'Uploading…' : 'Propose placements'}
          </button>
        </div>
        <p style={{ ...st.sub, fontSize: 12 }}>
          {access && access.maxUploadMb ? 'Up to ' + access.maxUploadMb + ' MB. ' : ''}
          Scale is read from the title block when possible; enter it if the sheet has none. DWG files need to be saved as DXF first.
        </p>
      </div>


      {rulesMsg && (
        <div role="status" onClick={() => setRulesMsg(null)}
          style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 1000, maxWidth: 'min(560px, 92vw)', cursor: 'pointer',
                   boxShadow: '0 6px 24px rgba(0,0,0,0.18)', borderRadius: 10, padding: '12px 16px', fontSize: 13, fontWeight: 600,
                   background: rulesMsg.ok ? '#166534' : '#b91c1c', color: '#fff' }}>
          {rulesMsg.ok ? '✓ ' : '⚠ '}{rulesMsg.text} <span style={{ opacity: 0.7, fontWeight: 400, marginLeft: 6 }}>(click to dismiss)</span>
        </div>
      )}

      <div style={st.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setRulesOpen(!rulesOpen)}>
          <h3 style={{ ...st.h3, margin: 0 }}>Placement rules{(profiles.find((p) => p.id === profile) || {}).label ? ' · ' + (profiles.find((p) => p.id === profile) || {}).label : ''}</h3>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{rulesOpen ? 'Hide ▲' : 'Show ▼'}</span>
        </div>
        {rulesOpen && !cfg && <p style={st.sub}>Loading…</p>}
        {rulesOpen && cfg && (
          <div style={{ marginTop: 12 }}>
            <p style={{ ...st.sub, marginTop: 0 }}>
              {cfg.customRules ? 'Your firm\'s custom rules are in use.' : 'Built-in rules are in use.'} When you upload a Revit model that lists a requirement for a room
              (<code>requirements</code> on the room), the model wins. These rules only apply to rooms the model does not cover, and to PDF/DXF plans.
            </p>
            {editing ? (
              <RulesEditor cfg={cfg} busy={false} onSave={saveEdited} onCancel={() => setEditing(false)} />
            ) : (<>
            <table style={{ width: '100%', borderCollapse: 'collapse', margin: '10px 0' }}>
              <thead><tr><th style={st.th}>Rule</th><th style={st.th}>Applies to rooms matching</th><th style={st.th}>Result</th></tr></thead>
              <tbody>
                {(cfg.rules || []).map((r, i) => (
                  <tr key={i}>
                    <td style={st.td}>{r.name}</td>
                    <td style={{ ...st.td, color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>{r.match}{r.exclude ? '  (not: ' + r.exclude + ')' : ''}</td>
                    <td style={st.td}>{r.action === 'skip' ? 'skip' : r.per_sf ? '1 per ' + r.per_sf + ' SF' + (r.max ? ' (max ' + r.max + ')' : '') : (r.count != null ? r.count + ' each' : '–')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ ...st.row, marginBottom: 6 }}>
              <div>
                <label style={st.label}>Rooms the Revit model has no requirement for</label>
                <select style={{ ...st.input, minWidth: 300 }} value={(cfg.options && cfg.options.model_fallback) || 'rules'} onChange={(e) => setFallback(e.target.value)}>
                  <option value="rules">Use these rules</option>
                  <option value="skip">Leave them alone (model only)</option>
                </select>
              </div>
              <button style={st.btnPrimary} onClick={() => setEditing(true)}>Edit rules</button>
              <button style={st.btn} onClick={downloadRules}>Download rules (.json)</button>
              <button style={st.btn} onClick={() => rulesInput.current && rulesInput.current.click()}>Upload rules…</button>
              <input ref={rulesInput} type="file" accept=".json,application/json" style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; uploadRules(f); }} />
              {cfg.customRules && <button style={st.btn} onClick={resetRules}>Reset to built-in</button>}
            </div>
            <div style={{ ...st.row, marginBottom: 6 }}>
              <div>
                <label style={st.label}>Revit family name (optional)</label>
                <input style={{ ...st.input, minWidth: 220 }} value={famName} placeholder="e.g. Data Outlet" onChange={(e) => setFamName(e.target.value)} />
              </div>
              <div>
                <label style={st.label}>Family type (optional)</label>
                <input style={{ ...st.input, minWidth: 180 }} value={famType} placeholder="e.g. Plain" onChange={(e) => setFamType(e.target.value)} />
              </div>
              <button style={st.btn} onClick={saveFamily}>Save family</button>
            </div>
            <p style={{ ...st.sub, fontSize: 12 }}>If the family is set, Revit places it without asking. Leave blank to choose in Revit each time. Use "Edit rules" to change them here, or download the file, edit it, and upload it back. Rules are checked before they are saved.</p>
            </>)}
          </div>
        )}
      </div>

      {job && (
        <div style={st.card}>
          <h3 style={st.h3}>2. Review {job.filename ? '– ' + job.filename : ''}{job.params && job.params.profileLabel ? ' · ' + job.params.profileLabel : ''}</h3>

          {job.status === 'queued' && (
            <div style={st.warn}>Waiting in line{job.queue && job.queue.waiting ? ' (' + job.queue.waiting + ' ahead)' : ''}…</div>
          )}
          {job.status === 'running' && <div style={st.warn}>Working on it. Large plans can take a minute or two.</div>}
          {job.status === 'failed' && <div style={st.err}>This run failed: {job.error || 'unknown error'}. Check the file and scale, then try again. A failed run doesn’t count against your limit.</div>}

          {job.status === 'done' && (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: '#374151', marginBottom: 12 }}>
                <span><b>{proposals.length}</b> proposed</span>
                <span><b>{(job.rooms || []).length}</b> rooms</span>
                <span style={{ color: '#166534' }}><b>{approvals.approved}</b> approved</span>
                <span style={{ color: '#991b1b' }}><b>{approvals.rejected}</b> rejected</span>
                <span><b>{approvals.pending}</b> pending</span>
              </div>

              {shownWarnings.length > 0 && (
                <div style={st.warn}>
                  {shownWarnings.slice(0, 6).map((w, i) => <div key={i}>• {typeof w === 'string' ? w : JSON.stringify(w)}</div>)}
                </div>
              )}

              {previewUrl && (
                <ZoomImage src={previewUrl} alt="Plan preview"
                  legend="Magenta dots = high-confidence placements, orange = needs review. Blue circles (Revit models) = doors." />
              )}

              {(job.flagged || []).length > 0 && (
                <div style={{ ...st.warn, background: '#fef9c3' }}>
                  <b>Needs a human look ({job.flagged.length}):</b> we skipped these rather than guess.
                  <div style={{ marginTop: 4 }}>
                    {job.flagged.slice(0, 12).map((f, i) => (
                      <div key={i}>• {[f.number || f.room_number, f.name || f.room_name].filter(Boolean).join(' ') || 'Room'}{f.reason || f.note ? ' – ' + (f.reason || f.note) : ''}</div>
                    ))}
                    {job.flagged.length > 12 && <div>…and {job.flagged.length - 12} more</div>}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                <button style={st.btn} onClick={() => decide(null, 'approve_high')}>Approve high-confidence</button>
                <button style={st.btn} onClick={() => decide(null, 'approve_all')}>Approve all</button>
                <button style={st.btn} onClick={() => decide(null, 'reject_all')}>Reject all</button>
                <button style={st.btn} onClick={() => decide(null, 'reset')}>Reset</button>
              </div>

              {proposals.length === 0 ? (
                <p style={st.sub}>
                  No placements were proposed.
                  {job.stats && job.stats.rooms_skipped > 0
                    ? ' ' + job.stats.rooms_skipped + ' room' + (job.stats.rooms_skipped === 1 ? '' : 's') + ' did not match any ' + ((job.params && job.params.profileLabel) || 'discipline') + ' rule, so ' + (job.stats.rooms_skipped === 1 ? 'it was' : 'they were') + ' skipped. Edit the rules to cover those room types, or switch discipline.'
                    : ' Check the warnings above, the scale, and that the plan has room labels.'}
                </p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      <th style={st.th}>Room</th><th style={st.th}>Drop</th><th style={st.th}>Confidence</th><th style={st.th}>Status</th><th style={st.th}></th>
                    </tr></thead>
                    <tbody>
                      {Object.entries(byRoom).map(([k, list]) => list.map((p, i) => (
                        <tr key={p.id}>
                          <td style={st.td}>{i === 0 ? (<><b>{p.room_number || '–'}</b> {p.room_name}</>) : ''}</td>
                          <td style={{ ...st.td, color: '#6b7280' }}>{p.id}{p.edited ? ' (moved)' : ''}{p.source === 'model' ? <span style={{ ...st.pill, marginLeft: 6, background: '#e0f2fe', color: '#075985' }}>from model</span> : null}</td>
                          <td style={st.td}>{p.confidence != null ? Math.round(p.confidence * 100) + '%' : '–'}</td>
                          <td style={st.td}><span style={{ ...st.pill, ...(STATUS_STYLE[p.status] || STATUS_STYLE.pending) }}>{p.status}</span></td>
                          <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                            <button style={{ ...st.btn, padding: '4px 10px' }} onClick={() => decide([{ id: p.id, status: p.status === 'approved' ? 'pending' : 'approved' }])}>{p.status === 'approved' ? 'Undo' : 'Approve'}</button>{' '}
                            <button style={{ ...st.btn, padding: '4px 10px' }} onClick={() => decide([{ id: p.id, status: p.status === 'rejected' ? 'pending' : 'rejected' }])}>{p.status === 'rejected' ? 'Undo' : 'Reject'}</button>
                          </td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {job && job.status === 'done' && proposals.length > 0 && (
        <div style={st.card}>
          <h3 style={st.h3}>3. Export approved placements</h3>
          <p style={st.sub}>Only the placements you approved are included.{approvals.pending > 0 ? ' ' + approvals.pending + ' still pending will be left out.' : ''}</p>
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button style={{ ...st.btnPrimary, ...((busy || approvals.approved === 0) ? st.btnOff : {}) }} disabled={busy || approvals.approved === 0} onClick={doExport}>
              {busy ? 'Exporting…' : 'Export ' + approvals.approved + ' approved'}
            </button>
            {exported.map((k) => (
              <button key={k} style={st.btn} onClick={() => saveBlob(getAuthToken, '/jobs/' + job.jobId + '/files/' + k, downloadName(job, k)).catch((e) => setError(e.message))}>
                ⬇ {FILE_LABEL[k] || k}
              </button>
            ))}
          </div>
          <p style={{ ...st.sub, fontSize: 12 }}>Changed an approval after exporting? Export again to refresh the files.</p>
        </div>
      )}

      {recent.length > 0 && (
        <div style={st.card}>
          <h3 style={st.h3}>Recent runs <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12 }}>(kept for 48 hours)</span></h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {recent.slice(0, 10).map((j) => (
                <tr key={j.jobId}>
                  <td style={st.td}>{j.filename}</td>
                  <td style={{ ...st.td, color: '#6b7280' }}>{KIND_LABEL[j.kind] || j.kind}{j.params && j.params.profileLabel ? ' · ' + j.params.profileLabel : ''}</td>
                  <td style={st.td}>{j.status}{j.status === 'done' && j.approvals ? ' · ' + j.approvals.approved + ' approved' : ''}</td>
                  <td style={{ ...st.td, color: '#6b7280' }}>{new Date(j.createdAt).toLocaleString()}</td>
                  <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                    <button style={{ ...st.btn, padding: '4px 10px' }} onClick={() => openJob(j.jobId)}>Open</button>{' '}
                    <button style={{ ...st.btn, padding: '4px 10px' }} onClick={() => removeJob(j.jobId)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {builds.length > 0 && (
        <div style={st.card}>
          <h3 style={st.h3}>Revit add-in</h3>
          <p style={st.sub}>Install the add-in to export and place symbols inside Revit. Pick the build that matches your Revit year, unzip it, close Revit, and double-click Install.bat.</p>
          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {builds.map((b) => (
              <button key={b.file} style={st.btn}
                onClick={() => saveBlob(getAuthToken, '/addin/download?file=' + encodeURIComponent(b.file), b.file).catch((e) => setError(e.message))}>
                ⬇ Revit {b.revit} · v{b.version} ({fmtMb(b.size)})
              </button>
            ))}
          </div>
          <p style={{ ...st.sub, fontSize: 12 }}>Early builds may be unsigned, so Windows or Revit may warn before installing. Your IT contact can approve it.</p>
        </div>
      )}
    </div>
  );
}

// Zoomable / pannable image: mouse wheel (or +/-) to zoom at the cursor, drag to pan, Fit to reset.
// ---------- Rules editor (alternative to editing the JSON file) ----------
const WORDS_OK = /^[A-Za-z0-9][A-Za-z0-9 /&-]*$/;
function toWords(m) {   // "OFFICE|PRIVATE OFFICE" -> "OFFICE, PRIVATE OFFICE"; adv=true when it is a real pattern
  if (!m) return { adv: false, text: '' };
  const parts = String(m).split('|').map((x) => x.trim());
  return parts.every((x) => WORDS_OK.test(x)) ? { adv: false, text: parts.join(', ') } : { adv: true, text: String(m) };
}
function fromWords(text, adv) {
  if (adv) return text.trim();
  return text.split(',').map((x) => x.trim()).filter(Boolean).map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
}
function rowFromRule(r) {
  const m = toWords(r.match), x = toWords(r.exclude);
  return { name: r.name || '', mAdv: m.adv, match: m.text, xAdv: x.adv, exclude: x.text,
           skip: r.action === 'skip', reason: r.reason || '', count: r.count != null ? String(r.count) : '1',
           per_sf: r.per_sf != null ? String(r.per_sf) : '', max: r.max != null ? String(r.max) : '',
           keep: { ...(r.offset_ft != null ? { offset_ft: r.offset_ft } : {}), ...(r.min_sep_ft != null ? { min_sep_ft: r.min_sep_ft } : {}) } };
}
function ruleFromRow(w) {
  const o = { ...w.keep, name: w.name.trim() || 'Rule', match: fromWords(w.match, w.mAdv) };
  const ex = fromWords(w.exclude, w.xAdv); if (ex) o.exclude = ex;
  if (w.skip) { o.action = 'skip'; if (w.reason.trim()) o.reason = w.reason.trim(); }
  else { o.count = Number(w.count); if (w.per_sf !== '') o.per_sf = Number(w.per_sf); if (w.max !== '') o.max = Number(w.max); }
  return o;
}
function validateRows(rows) {
  for (let i = 0; i < rows.length; i++) {
    const w = rows[i], at = 'Rule ' + (i + 1) + (w.name ? ' (' + w.name + ')' : '');
    if (!fromWords(w.match, w.mAdv)) return at + ': enter at least one room-name word to match.';
    try { new RegExp(fromWords(w.match, w.mAdv), 'i'); if (fromWords(w.exclude, w.xAdv)) new RegExp(fromWords(w.exclude, w.xAdv), 'i'); } catch (e) { return at + ': that pattern is not valid.'; }
    if (!w.skip) {
      const c = Number(w.count);
      if (w.count === '' || !Number.isInteger(c) || c < 0 || c > 20) return at + ': "How many" must be a whole number from 0 to 20.';
      if (w.per_sf !== '' && !(Number(w.per_sf) > 0)) return at + ': "One more per … SF" must be a positive number.';
      if (w.max !== '' && !(Number(w.max) >= 1)) return at + ': "Never more than" must be 1 or more.';
    }
  }
  return '';
}

function RulesEditor({ cfg, onSave, onCancel, busy }) {
  const [rows, setRows] = useState(() => (cfg.rules || []).map(rowFromRule));
  const [offset, setOffset] = useState(cfg.options && cfg.options.offset_ft != null ? String(cfg.options.offset_ft) : '');
  const [sep, setSep] = useState(cfg.options && cfg.options.min_sep_ft != null ? String(cfg.options.min_sep_ft) : '');
  const [testName, setTestName] = useState('');
  const [err, setErr] = useState('');
  const upd = (i, patch) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const move = (i, d) => setRows((rs) => { const j = i + d; if (j < 0 || j >= rs.length) return rs; const c = rs.slice(); [c[i], c[j]] = [c[j], c[i]]; return c; });
  const del = (i) => setRows((rs) => rs.filter((_, k) => k !== i));
  const add = () => setRows((rs) => [...rs, rowFromRule({ name: 'New rule', match: '', count: 1 })]);

  let hit = -1;
  if (testName.trim()) {
    hit = rows.findIndex((w) => { try { const m = fromWords(w.match, w.mAdv); if (!m || !new RegExp(m, 'i').test(testName.trim())) return false;
      const x = fromWords(w.exclude, w.xAdv); return !(x && new RegExp(x, 'i').test(testName.trim())); } catch (e) { return false; } });
  }
  function save() {
    if (!rows.length) return setErr('Add at least one rule.');
    const bad = validateRows(rows);
    if (bad) return setErr(bad);
    for (const [v, label] of [[offset, 'Distance from corner/door'], [sep, 'Minimum spacing']]) if (v !== '' && !(Number(v) > 0)) return setErr(label + ' must be a positive number (or blank for the default).');
    setErr(''); onSave(rows.map(ruleFromRow), { offset_ft: offset === '' ? null : Number(offset), min_sep_ft: sep === '' ? null : Number(sep) });
  }
  const cell = { ...st.input, minWidth: 0, width: '100%', boxSizing: 'border-box' };
  const tiny = { ...st.btn, padding: '3px 8px', fontWeight: 500 };
  return (
    <div>
      <p style={{ ...st.sub, marginTop: 0 }}>
        Rules are checked <b>top to bottom, and the first one that matches the room name is used</b>. Put specific rules above general ones
        (for example "Open office" above "Office"). Matching ignores upper/lower case and looks for the words anywhere in the room name.
      </p>
      {rows.map((w, i) => (
        <div key={i} style={{ border: '1px solid ' + (hit === i ? '#D4AF37' : '#e5e7eb'), background: hit === i ? '#fffbeb' : '#fafafa', borderRadius: 10, padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>{i + 1}</span>
            <input style={{ ...cell, flex: '1 1 180px', width: 'auto', fontWeight: 600 }} value={w.name} placeholder="Rule name" onChange={(e) => upd(i, { name: e.target.value })} />
            <select style={{ ...st.input, minWidth: 0 }} value={w.skip ? 'skip' : 'place'} onChange={(e) => upd(i, { skip: e.target.value === 'skip' })}>
              <option value="place">Place symbols</option><option value="skip">Skip these rooms</option>
            </select>
            <button style={tiny} onClick={() => move(i, -1)} disabled={i === 0} title="Move up">↑</button>
            <button style={tiny} onClick={() => move(i, 1)} disabled={i === rows.length - 1} title="Move down">↓</button>
            <button style={{ ...tiny, color: '#b91c1c' }} onClick={() => del(i)} title="Delete rule">Delete</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            <div>
              <label style={st.label}>{w.mAdv ? 'Room name pattern (advanced)' : 'Room name contains (commas between words)'}</label>
              <input style={{ ...cell, fontFamily: w.mAdv ? 'monospace' : 'inherit' }} value={w.match} placeholder="OFFICE, PRIVATE OFFICE" onChange={(e) => upd(i, { match: e.target.value })} />
            </div>
            <div>
              <label style={st.label}>{w.xAdv ? 'But not (pattern, advanced)' : 'But not these words (optional)'}</label>
              <input style={{ ...cell, fontFamily: w.xAdv ? 'monospace' : 'inherit' }} value={w.exclude} placeholder="OPEN" onChange={(e) => upd(i, { exclude: e.target.value })} />
            </div>
            {w.skip ? (
              <div>
                <label style={st.label}>Reason shown in the report</label>
                <input style={cell} value={w.reason} placeholder="circulation" onChange={(e) => upd(i, { reason: e.target.value })} />
              </div>
            ) : (
              <>
                <div>
                  <label style={st.label}>How many per room</label>
                  <input style={cell} type="number" min="0" max="20" value={w.count} onChange={(e) => upd(i, { count: e.target.value })} />
                </div>
                <div>
                  <label style={st.label}>One more per … SF (optional)</label>
                  <input style={cell} type="number" min="1" value={w.per_sf} placeholder="e.g. 150" onChange={(e) => upd(i, { per_sf: e.target.value })} />
                </div>
                <div>
                  <label style={st.label}>Never more than (optional)</label>
                  <input style={cell} type="number" min="1" value={w.max} placeholder="e.g. 6" onChange={(e) => upd(i, { max: e.target.value })} />
                </div>
              </>
            )}
          </div>
        </div>
      ))}
      <button style={st.btn} onClick={add}>+ Add a rule</button>

      <div style={{ ...st.row, marginTop: 16 }}>
        <div>
          <label style={st.label}>Test a room name</label>
          <input style={{ ...st.input, minWidth: 220 }} value={testName} placeholder="e.g. PRIVATE OFFICE" onChange={(e) => setTestName(e.target.value)} />
        </div>
        <div style={{ fontSize: 13, color: '#374151', paddingBottom: 8 }}>
          {testName.trim() ? (hit >= 0
            ? <>Rule <b>{hit + 1}</b> ({rows[hit].name}) {rows[hit].skip ? 'skips this room.' : 'places ' + rows[hit].count + (rows[hit].per_sf ? ' (more in large rooms)' : '') + '.'}</>
            : 'No rule matches, so this room would be skipped and reported.') : ''}
        </div>
      </div>
      <div style={{ ...st.row, marginTop: 12 }}>
        <div>
          <label style={st.label}>Distance from corner/door, ft (blank = default)</label>
          <input style={{ ...st.input, width: 120, minWidth: 0 }} type="number" step="0.25" value={offset} placeholder={String((cfg.defaultOptions || {}).offset_ft ?? '')} onChange={(e) => setOffset(e.target.value)} />
        </div>
        <div>
          <label style={st.label}>Minimum spacing between symbols, ft</label>
          <input style={{ ...st.input, width: 120, minWidth: 0 }} type="number" step="0.5" value={sep} placeholder={String((cfg.defaultOptions || {}).min_sep_ft ?? '')} onChange={(e) => setSep(e.target.value)} />
        </div>
      </div>
      {err && <div style={{ ...st.err, marginTop: 16, marginBottom: 0 }}>{err}</div>}
      <div style={{ ...st.row, marginTop: 16 }}>
        <button style={{ ...st.btnPrimary, ...(busy ? st.btnOff : {}) }} disabled={busy} onClick={save}>Save rules</button>
        <button style={st.btn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function ZoomImage({ src, alt, legend }) {
  const box = useRef(null);
  const [nat, setNat] = useState(null);          // natural image size
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const [tall, setTall] = useState(false);
  const drag = useRef(null);
  const H = tall ? Math.max(480, Math.round(window.innerHeight * 0.82)) : 520;

  const fit = useCallback((n, h) => {
    const el = box.current; if (!el || !n) return;
    const w = el.clientWidth;
    const s = Math.min(w / n.w, (h || H) / n.h);
    setView({ s, x: (w - n.w * s) / 2, y: ((h || H) - n.h * s) / 2 });
  }, [H]);

  useEffect(() => { if (nat) fit(nat); }, [nat, tall, fit]);

  const zoomAt = useCallback((factor, cx, cy) => {
    setView((v) => {
      const s = Math.min(12, Math.max(0.05, v.s * factor));
      const k = s / v.s;
      return { s, x: cx - (cx - v.x) * k, y: cy - (cy - v.y) * k };
    });
  }, []);

  useEffect(() => {      // wheel must be a non-passive listener to stop the page scrolling
    const el = box.current; if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      zoomAt(e.deltaY < 0 ? 1.18 : 1 / 1.18, e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt, src]);

  const btn = { ...st.btn, padding: '4px 10px', minWidth: 34 };
  const center = () => { const el = box.current; return [el ? el.clientWidth / 2 : 0, H / 2]; };
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
        <button style={btn} title="Zoom in" onClick={() => zoomAt(1.4, ...center())}>＋</button>
        <button style={btn} title="Zoom out" onClick={() => zoomAt(1 / 1.4, ...center())}>－</button>
        <button style={btn} onClick={() => nat && fit(nat)}>Fit</button>
        <button style={btn} onClick={() => setTall((t) => !t)}>{tall ? 'Smaller' : 'Taller'}</button>
        <button style={btn} onClick={() => window.open(src, '_blank')}>Open in new tab</button>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Scroll to zoom, drag to move</span>
      </div>
      <div ref={box}
        style={{ position: 'relative', height: H, overflow: 'hidden', background: '#000', border: '1px solid #e5e7eb', borderRadius: 8, cursor: drag.current ? 'grabbing' : 'grab', touchAction: 'none' }}
        onPointerDown={(e) => { drag.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y }; e.currentTarget.setPointerCapture(e.pointerId); }}
        onPointerMove={(e) => { const d = drag.current; if (d) setView((v) => ({ ...v, x: d.vx + e.clientX - d.x, y: d.vy + e.clientY - d.y })); }}
        onPointerUp={() => { drag.current = null; }}
        onPointerCancel={() => { drag.current = null; }}
        onDoubleClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); zoomAt(2, e.clientX - r.left, e.clientY - r.top); }}>
        <img src={src} alt={alt} draggable={false}
          onLoad={(e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
          style={{ position: 'absolute', left: 0, top: 0, transformOrigin: '0 0', transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`,
                   imageRendering: view.s > 2 ? 'pixelated' : 'auto', maxWidth: 'none', userSelect: 'none' }} />
      </div>
      {legend && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{legend}</div>}
    </div>
  );
}

function countApprovals(list) {
  const out = { approved: 0, rejected: 0, pending: 0 };
  (list || []).forEach((p) => { out[p.status] = (out[p.status] || 0) + 1; });
  return out;
}
